import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserInfo, getOrCreateIdVerificationToken } from '../../../store/slices/user/asyncThunks';
import { selectUser, selectUserActionsPending } from '../../../store/slices/user/slice';
import { notifyError } from '../../../utils/notify';
import BaseModal from '../utils/BaseModal/BaseModal';
import {
  CloseButton,
  ContentContainer,
  Footer,
  IdVerificationContent,
  List,
  ListItem,
  Paragraph,
  SectionTitle,
  UnderstandButton,
} from './MassPayNoteModal.styles';

const STEP = {
  NOTE: 'NOTE',
  VERIFICATION: 'VERIFICATION',
};

const MassPayNoteModal = ({ modalRef, createMassPayModalRef }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { getOrCreateIdVerificationToken: getOrCreateIdVerificationTokenPending } =
    useSelector(selectUserActionsPending);
  const [step, setStep] = useState(STEP.NOTE);
  const [openCreateMassPayAfterClose, setOpenCreateMassPayAfterClose] = useState(false);

  const handleClose = () => {
    modalRef.current.hide();
  };

  const handleAfterClose = () => {
    setStep(STEP.NOTE);

    if (openCreateMassPayAfterClose) {
      setOpenCreateMassPayAfterClose(false);
      createMassPayModalRef?.current?.show();
    }
  };

  const openCreateMassPayModal = () => {
    dispatch(fetchUserInfo());
    setOpenCreateMassPayAfterClose(true);
    handleClose();
  };

  const handleVerificationContinue = () => {
    if (user?.documentVerification?.status === 'Approved') {
      openCreateMassPayModal();
      return;
    }

    const popup = window.open('');

    dispatch(getOrCreateIdVerificationToken())
      .unwrap()
      .then(({ sessionUrl, sessionStatus }) => {
        if (sessionStatus === 'Approved') {
          popup.close();
          openCreateMassPayModal();
          return;
        }
        popup.location.href = sessionUrl;
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  const isUserVerified = user?.documentVerification?.status === 'Approved';

  return (
    <BaseModal
      title={step === STEP.NOTE ? 'Important: MassPay Account Requirements' : 'ID Verification'}
      ref={modalRef}
      onClose={handleClose}
      onAfterClose={handleAfterClose}>
      <ContentContainer>
        {step === STEP.NOTE ? (
          <>
            <Paragraph>
              To ensure compliance and secure payouts, all creators must use a MassPay account that is registered in
              their own legal name.
            </Paragraph>

            <div>
              <SectionTitle>This means:</SectionTitle>
              <List>
                <ListItem>
                  The name on your MassPay account must match <b>your personal legal name</b>, or
                </ListItem>
                <ListItem>
                  If you are operating through a business, the account must be in <b>your company's legal name</b>, and
                  you must be an authorized representative of that company.
                </ListItem>
              </List>
            </div>

            <div>
              <SectionTitle>Please note:</SectionTitle>
              <List>
                <ListItem>
                  Accounts registered under <b>another person's name</b> (e.g., spouse, family member, or any third
                  party) are
                  <b> not permitted</b>.
                </ListItem>
                <ListItem>
                  Payouts can only be sent to the <b>verified account holder</b>.
                </ListItem>
                <ListItem>Our system does not allow third-party payments under any circumstances.</ListItem>
              </List>
            </div>

            <Footer>
              <CloseButton title="Close" onClick={handleClose} />
              <UnderstandButton title="I understand" onClick={() => setStep(STEP.VERIFICATION)} />
            </Footer>
          </>
        ) : (
          <>
            <IdVerificationContent>
              {isUserVerified ? (
                <Paragraph>
                  Your ID verification is already complete. Continue to open your MassPay account setup.
                </Paragraph>
              ) : (
                <>
                  <Paragraph>
                    Before creating your MassPay account, please complete a quick ID verification.
                    <br /> This helps keep your account secure, meets payment requirements, and ensures payouts are sent
                    only to the verified account holder.
                  </Paragraph>
                  <br />
                  <Paragraph>
                    Note: Finish the verification in the opened tab, then return here and click Continue again. Once
                    your verification is approved, we'll open the MassPay account setup for you.
                  </Paragraph>
                </>
              )}
            </IdVerificationContent>

            <Footer>
              <CloseButton title="Back" onClick={() => setStep(STEP.NOTE)} />
              <UnderstandButton
                title="Continue"
                onClick={handleVerificationContinue}
                isLoading={getOrCreateIdVerificationTokenPending}
              />
            </Footer>
          </>
        )}
      </ContentContainer>
    </BaseModal>
  );
};

export default MassPayNoteModal;
